import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { parseTags, getAllTags, getCategoryColor, getTagCounts, getMajorCategoryCounts, designMatchesTag, createStructuredTags, sortTagsAlphabetically } from '../utils/tagUtils';
import { fetchCategories } from '../utils/categories';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import Toast from '../components/Toast';
import { API_BASE_URL } from '../config/api';

// Create axios instance with auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function AdminPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [orientationFilter, setOrientationFilter] = useState(null); // null = all, 'horizontal', 'vertical'
  const [selectedTag, setSelectedTag] = useState(null);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', orientation: '', tags: '', price: '', product_type: 'Physical', mockups: [] });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [toast, setToast] = useState(null);
  const [showTagEditor, setShowTagEditor] = useState(false);
  const [tagEditor, setTagEditor] = useState({ majorCategory: '', minorTags: [] });
  const [newMinorTag, setNewMinorTag] = useState('');
  const [majorCategories, setMajorCategories] = useState([]);
  const [mockupForm, setMockupForm] = useState({ name: '', image_url: '', thumbnail_url: '' });
  const [showMockupEditor, setShowMockupEditor] = useState(false);
  const sortedTagEditorMinorTags = sortTagsAlphabetically(tagEditor.minorTags);
  
  // Refs for design cards to enable scrolling
  const designRefs = useRef({});

  useEffect(() => {
    document.title = 'Design Store Admin';
    fetchDesigns();
    // Load categories
    fetchCategories().then(categories => {
      setMajorCategories(categories.categories.map(c => c.name));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
  }, []);

  // Auto-open design if ID is in URL
  useEffect(() => {
    const designId = searchParams.get('designId');
    if (designId && designs.length > 0 && !editingId) {
      const design = designs.find(d => d.id === designId);
      if (design) {
        // Set edit form directly to avoid dependency issues
        const { majorCategory, minorTags } = parseTags(design.tags || '');
        setEditingId(design.id);
        setEditForm({
          title: design.title || '',
          description: design.description || '',
          orientation: design.orientation || 'horizontal',
          tags: design.tags || '',
          price: design.price !== undefined && design.price !== null ? design.price.toString() : '',
          product_type: design.product_type || 'Physical',
          mockups: Array.isArray(design.mockups) ? design.mockups : []
        });
        setTagEditor({
          majorCategory: majorCategory || '',
          minorTags: minorTags || []
        });
        setShowTagEditor(false);
        setSaveSuccess(null);
        // Remove the query param after opening
        setSearchParams({});
        
        // Scroll to the design card after a short delay to ensure DOM has updated
        setTimeout(() => {
          const designRef = designRefs.current[design.id];
          if (designRef) {
            designRef.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    }
  }, [designs, searchParams, editingId, setSearchParams]);

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/designs`);
      if (response.data.success) {
        setDesigns(response.data.data);
      } else {
        setError('Failed to load designs');
      }
    } catch (err) {
      console.error('Error fetching designs:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setError(err.response?.data?.error || err.message || 'Failed to load designs');
    } finally {
      setLoading(false);
    }
  };


  const handleEdit = (design) => {
    setEditingId(design.id);
    const { majorCategory, minorTags } = parseTags(design.tags || '');
    setEditForm({
      title: design.title || '',
      description: design.description || '',
      orientation: design.orientation || 'horizontal',
      tags: design.tags || '',
      price: design.price !== undefined && design.price !== null ? design.price.toString() : '',
      product_type: design.product_type || 'Physical',
      mockups: Array.isArray(design.mockups) ? design.mockups : []
    });
    setTagEditor({
      majorCategory: majorCategory || '',
      minorTags: minorTags || []
    });
    setShowTagEditor(true); // Show tag editor immediately when editing
    setSaveSuccess(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({ title: '', description: '', orientation: '', tags: '', price: '', product_type: 'Physical', mockups: [] });
    setTagEditor({ majorCategory: '', minorTags: [] });
    setShowTagEditor(false);
    setNewMinorTag('');
    setMockupForm({ name: '', image_url: '', thumbnail_url: '' });
    setShowMockupEditor(false);
    setSaveSuccess(null);
  };

  const handleSave = async (designId) => {
    try {
      setSaving(true);
      setError(null);
      
      // Verify token exists before making request
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setError('You are not logged in. Please log in again.');
        navigate('/login');
        return;
      }
      
      // Build structured tags object from tag editor
      const structuredTags = createStructuredTags(
        tagEditor.majorCategory || null,
        tagEditor.minorTags || [],
        []
      );
      
      console.log('Saving design:', designId, 'with token:', token ? 'present' : 'missing');
      
      // Build update payload
      const updatePayload = {
        title: editForm.title,
        description: editForm.description,
        orientation: editForm.orientation,
        tags: structuredTags
      };
      
      // Add price if provided (only include if it has a value)
      if (editForm.price && editForm.price.trim() !== '') {
        const priceValue = parseFloat(editForm.price);
        if (!isNaN(priceValue) && priceValue >= 0) {
          updatePayload.price = priceValue;
          // Add product_type if price is set
          updatePayload.product_type = editForm.product_type || 'Physical';
        }
      }
      // Note: If price is empty, we don't include it in the payload
      // The server will only update fields that are provided
      
      // Add mockups if provided (only include if it's an array)
      if (Array.isArray(editForm.mockups)) {
        updatePayload.mockups = editForm.mockups;
      }
      
      console.log('Sending update payload:', updatePayload);
      
      const response = await authAxios.put(`${API_BASE_URL}/designs/${designId}`, updatePayload);

      console.log('Save response:', response.data);

      if (response.data.success) {
        // Update local state
        setDesigns(designs.map(d => {
          if (d.id === designId) {
            const updated = {
              ...d,
              title: editForm.title,
              description: editForm.description,
              orientation: editForm.orientation,
              tags: structuredTags
            };
            // Add price and product_type if set
            if (updatePayload.price !== null && updatePayload.price !== undefined) {
              updated.price = updatePayload.price;
              updated.product_type = updatePayload.product_type || 'Physical';
            } else {
              // Remove price and product_type if cleared
              delete updated.price;
              delete updated.product_type;
            }
            // Add mockups
            if (Array.isArray(updatePayload.mockups)) {
              updated.mockups = updatePayload.mockups;
            }
            return updated;
          }
          return d;
        }));
        setEditingId(null);
        setSaveSuccess(designId);
        setToast({ message: 'Design saved successfully!', type: 'success' });
        setTimeout(() => setSaveSuccess(null), 3000);
      } else {
        const errorMsg = response.data.error || 'Failed to save changes';
        setError(errorMsg);
        setToast({ message: errorMsg, type: 'error' });
      }
    } catch (err) {
      console.error('Error saving design:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Token expired or invalid, redirect to login
        console.error('Authentication failed, redirecting to login');
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save changes';
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMinorTag = () => {
    const normalizedTag = newMinorTag.trim().toLowerCase();
    if (normalizedTag && !tagEditor.minorTags.includes(normalizedTag)) {
      setTagEditor({
        ...tagEditor,
        minorTags: [...tagEditor.minorTags, normalizedTag]
      });
      setNewMinorTag('');
    }
  };

  const handleRemoveMinorTag = (tagToRemove) => {
    setTagEditor({
      ...tagEditor,
      minorTags: tagEditor.minorTags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleToggleOrientation = async (designId, currentOrientation) => {
    try {
      setSaving(true);
      setError(null);
      const newOrientation = currentOrientation === 'horizontal' ? 'vertical' : 'horizontal';
      
      const response = await authAxios.put(`${API_BASE_URL}/designs/${designId}`, {
        orientation: newOrientation
      });

      if (response.data.success) {
        // Update local state
        setDesigns(designs.map(d => 
          d.id === designId 
            ? { ...d, orientation: newOrientation }
            : d
        ));
        setSaveSuccess(designId);
        setTimeout(() => setSaveSuccess(null), 2000);
      } else {
        setError('Failed to update orientation');
      }
    } catch (err) {
      console.error('Error updating orientation:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update orientation');
    } finally {
      setSaving(false);
    }
  };

  // Get tag statistics
  const tagCounts = useMemo(() => getTagCounts(designs), [designs]);
  const majorCategoryCounts = useMemo(() => 
    getMajorCategoryCounts(designs, majorCategories), 
    [designs, majorCategories]
  );
  
  // Get popular tags (top 10)
  const popularTags = useMemo(() => {
    const top = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag]) => tag);
    return sortTagsAlphabetically(top);
  }, [tagCounts]);

  // Get all tags in the selected category
  const categoryTags = useMemo(() => {
    if (!selectedTag) {
      return [];
    }
    
    // Check if selectedTag is a major category (case-insensitive comparison)
    const isMajorCategory = majorCategories.some(cat => 
      cat.toLowerCase() === selectedTag.toLowerCase()
    );
    
    if (!isMajorCategory) {
      return [];
    }
    
    // Get all designs with this major category (case-insensitive)
    const selectedCategoryLower = selectedTag.toLowerCase();
    const categoryDesigns = designs.filter(design => {
      if (!design.tags) return false;
      const { majorCategory } = parseTags(design.tags);
      return majorCategory && majorCategory.toLowerCase() === selectedCategoryLower;
    });
    
    // Extract all minor tags from these designs
    const tagCountsInCategory = {};
    categoryDesigns.forEach(design => {
      if (design.tags) {
        const { minorTags } = parseTags(design.tags);
        minorTags.forEach(tag => {
          tagCountsInCategory[tag] = (tagCountsInCategory[tag] || 0) + 1;
        });
      }
    });
    
    // Return sorted by count
    return Object.entries(tagCountsInCategory)
      .sort((a, b) =>
        a[0].toLowerCase().localeCompare(b[0].toLowerCase(), undefined, { sensitivity: 'base' })
      )
      .map(([tag, count]) => ({ tag, count }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- majorCategories from state, stable
  }, [designs, selectedTag]);

  // Filter designs based on search query, orientation, and tags
  const filteredDesigns = useMemo(() => {
    let filtered = designs;

    // Filter by orientation
    if (orientationFilter) {
      filtered = filtered.filter((design) => {
        const designOrientation = (design.orientation || 'horizontal').toLowerCase();
        return designOrientation === orientationFilter.toLowerCase();
      });
    }

    // Filter by tag
    if (selectedTag) {
      filtered = filtered.filter((design) => designMatchesTag(design, selectedTag));
    }

    // Filter by search query (includes tags)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((design) => {
        const title = (design.title || '').toLowerCase();
        const description = (design.description || '').toLowerCase();
        const id = (design.id || '').toLowerCase();
        const tags = design.tags ? getAllTags(design.tags).join(' ').toLowerCase() : '';
        return title.includes(query) || description.includes(query) || id.includes(query) || tags.includes(query);
      });
    }

    return filtered;
  }, [designs, searchQuery, orientationFilter, selectedTag]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading designs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs />

        {/* Quick Links */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/add-design"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Design
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/formats"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
              </svg>
              Formats
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <button
              onClick={() => navigate('/admin', { state: { activeTab: 'json-editor' } })}
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Design Editor
            </button>
          </div>
        </div>

        {/* Search Box and Filters */}
          <div className="mb-4 md:mb-6 flex flex-col sm:flex-row gap-3 md:gap-4 items-start sm:items-center">
            <div className="relative flex-1 w-full sm:max-w-md">
              <input
                type="text"
                placeholder="Search by ID, title, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-3 pl-9 md:pl-10 pr-4 text-sm md:text-base text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 md:pl-3 pointer-events-none">
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-2.5 md:pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Orientation Filter */}
            <div className="flex gap-1.5 md:gap-2 w-full sm:w-auto">
              <button
                onClick={() => setOrientationFilter(null)}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === null
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setOrientationFilter('horizontal')}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === 'horizontal'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Horizontal
              </button>
              <button
                onClick={() => setOrientationFilter('vertical')}
                className={`flex-1 sm:flex-none px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-colors ${
                  orientationFilter === 'vertical'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Vertical
              </button>
            </div>
          </div>

          {/* Active Filters */}
          {(selectedTag || searchQuery.trim()) && (
            <div className="mb-3 md:mb-4 flex flex-wrap gap-1.5 md:gap-2 items-center">
              <span className="text-xs md:text-sm text-gray-600">Active:</span>
              {selectedTag && (
                <button
                  onClick={() => setSelectedTag(null)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-blue-100 text-blue-800 rounded-full text-xs md:text-sm font-medium hover:bg-blue-200"
                >
                  <span className="max-w-[120px] md:max-w-none truncate">{selectedTag}</span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {searchQuery.trim() && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-gray-100 text-gray-800 rounded-full text-xs md:text-sm font-medium hover:bg-gray-200"
                >
                  <span className="max-w-[100px] md:max-w-none truncate">"{searchQuery}"</span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Tag Filters - Collapsible on Mobile */}
          <div className="mb-4 md:mb-6">
            {/* Mobile Toggle Button */}
            <div className="md:hidden mb-3">
              <button
                onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="text-sm font-medium">
                  {showFiltersMobile ? 'Hide Filters' : 'Show Filters'}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Filters Content - Hidden on mobile unless expanded */}
            <div className={`${showFiltersMobile ? 'block' : 'hidden'} md:block`}>
              <div className="mb-3">
                <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">Filter by Category:</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {Object.entries(majorCategoryCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, count]) => (
                      <button
                        key={category}
                        onClick={() => setSelectedTag(selectedTag === category ? null : category)}
                        className={`px-2 py-1 md:px-3 md:py-1 text-xs font-medium rounded-lg transition-colors ${
                          selectedTag === category
                            ? getCategoryColor(category) + ' border-2'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        <span className="hidden sm:inline">{category}</span>
                        <span className="sm:hidden">{category.split(' ')[0]}</span>
                        <span className="ml-1">({count})</span>
                      </button>
                    ))}
                </div>
              </div>
              
              {/* Show category tags when a category is selected, otherwise show popular tags */}
              {selectedTag && majorCategories.some(cat => cat.toLowerCase() === selectedTag.toLowerCase()) ? (
                categoryTags.length > 0 ? (
                  <div>
                    <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">
                      Tags in "{selectedTag}":
                    </p>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      {categoryTags.map(({ tag, count }) => (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(tag)}
                          className="px-2 py-0.5 md:px-3 md:py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          {tag} ({count})
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null
              ) : (
                !selectedTag && popularTags.length > 0 && (
                  <div>
                    <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">Popular Tags:</p>
                    <div className="flex flex-wrap gap-1.5 md:gap-2">
                      {popularTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(tag)}
                          className="px-2 py-0.5 md:px-3 md:py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          {tag} ({tagCounts[tag]})
                        </button>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <p className="text-sm text-gray-500">
            {searchQuery.trim() || orientationFilter || selectedTag
              ? `Showing ${filteredDesigns.length} of ${designs.length} designs`
              : `${designs.length} design${designs.length !== 1 ? 's' : ''} total`
            }
          </p>

        {/* Design Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDesigns.map((design) => (
            <div
              key={design.id}
              ref={(el) => {
                if (el) {
                  designRefs.current[design.id] = el;
                }
              }}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                design.orientation === 'vertical' 
                  ? 'border-2 border-green-200' 
                  : 'border border-gray-200'
              } ${
                editingId === design.id ? 'ring-2 ring-blue-500' : ''
              } ${saveSuccess === design.id ? 'ring-2 ring-green-500' : ''}`}
            >
              {/* Thumbnail */}
              <div className="aspect-square bg-gray-100 overflow-hidden">
                <img
                  src={`${API_BASE_URL.replace('/api', '')}/api/designs/${design.id}/thumbnail`}
                  alt={design.title}
                  width="400"
                  height="400"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = design.thumbnail_url;
                  }}
                />
              </div>

              {/* Card Content */}
              <div className="p-4">
                {editingId === design.id ? (
                  // Edit Mode
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        ID
                      </label>
                      <p className="text-sm text-gray-500 font-mono">{design.id}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows="2"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Orientation
                      </label>
                      <select
                        value={editForm.orientation}
                        onChange={(e) => setEditForm({ ...editForm, orientation: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                      </select>
                    </div>
                    
                    {/* Tags Section */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-700">
                          Tags
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowTagEditor(!showTagEditor)}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          {showTagEditor ? 'Hide Editor' : 'Edit Tags'}
                        </button>
                      </div>
                      
                      {showTagEditor ? (
                        <div className="space-y-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                          {/* Major Category */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Major Category
                            </label>
                            <select
                              value={tagEditor.majorCategory}
                              onChange={(e) => setTagEditor({ ...tagEditor, majorCategory: e.target.value })}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select category...</option>
                              {majorCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Minor Tags */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Minor Tags
                            </label>
                            <div className="flex gap-2 mb-2">
                              <input
                                type="text"
                                value={newMinorTag}
                                onChange={(e) => setNewMinorTag(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddMinorTag();
                                  }
                                }}
                                placeholder="Add tag..."
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={handleAddMinorTag}
                                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                              >
                                Add
                              </button>
                            </div>
                            
                            {/* Display minor tags */}
                            {tagEditor.minorTags.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {sortedTagEditorMinorTags.map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                  >
                                    {tag}
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveMinorTag(tag)}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Preview */}
                          {tagEditor.majorCategory && (
                            <div className="pt-2 border-t border-gray-300">
                              <p className="text-xs text-gray-600 mb-1">Preview:</p>
                                <p className="text-xs font-mono bg-white p-2 rounded border border-gray-300">
                                {[tagEditor.majorCategory, ...sortedTagEditorMinorTags].filter(Boolean).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                          {editForm.tags ? (
                            <div className="space-y-1">
                              {(() => {
                                const { majorCategory, minorTags } = parseTags(editForm.tags);
                                const sortedMinorTags = sortTagsAlphabetically(minorTags);
                                return (
                                  <>
                                    {majorCategory && (
                                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${getCategoryColor(majorCategory)}`}>
                                        {majorCategory}
                                      </span>
                                    )}
                                    {sortedMinorTags.map((tag, idx) => (
                                      <span key={idx} className="inline-block ml-1 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                                        {tag}
                                      </span>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">No tags assigned</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Mockup-Only Design Settings */}
                    <div className="pt-2 border-t border-gray-300">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-700">
                          Mockup-Only Design Settings
                        </label>
                        <span className="text-xs text-gray-500">
                          (Optional - for designs with pre-rendered mockups)
                        </span>
                      </div>
                      
                      {/* Price */}
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Price (for direct purchase)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editForm.price}
                          onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                          placeholder="Leave empty to use formats"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      {/* Product Type (only show if price is set) */}
                      {editForm.price && editForm.price.trim() !== '' && (
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Product Type
                          </label>
                          <select
                            value={editForm.product_type}
                            onChange={(e) => setEditForm({ ...editForm, product_type: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Physical">Physical</option>
                            <option value="Digital">Digital</option>
                          </select>
                        </div>
                      )}
                      
                      {/* Mockups Editor */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-700">
                            Mockups ({editForm.mockups.length})
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowMockupEditor(!showMockupEditor)}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            {showMockupEditor ? 'Hide Editor' : 'Edit Mockups'}
                          </button>
                        </div>
                        
                        {showMockupEditor ? (
                          <div className="space-y-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                            {/* Add Mockup Form */}
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={mockupForm.name}
                                onChange={(e) => setMockupForm({ ...mockupForm, name: e.target.value })}
                                placeholder="Mockup name"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <input
                                type="text"
                                value={mockupForm.image_url}
                                onChange={(e) => setMockupForm({ ...mockupForm, image_url: e.target.value })}
                                placeholder="Image URL (e.g., /assets/mockups/design-mockup1.jpg)"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <input
                                type="text"
                                value={mockupForm.thumbnail_url}
                                onChange={(e) => setMockupForm({ ...mockupForm, thumbnail_url: e.target.value })}
                                placeholder="Thumbnail URL (optional)"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (mockupForm.image_url || mockupForm.thumbnail_url) {
                                    setEditForm({
                                      ...editForm,
                                      mockups: [...editForm.mockups, {
                                        name: mockupForm.name || 'Mockup',
                                        image_url: mockupForm.image_url || '',
                                        thumbnail_url: mockupForm.thumbnail_url || ''
                                      }]
                                    });
                                    setMockupForm({ name: '', image_url: '', thumbnail_url: '' });
                                  }
                                }}
                                className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                              >
                                Add Mockup
                              </button>
                            </div>
                            
                            {/* Display mockups */}
                            {editForm.mockups.length > 0 && (
                              <div className="space-y-2 pt-2 border-t border-gray-300">
                                {editForm.mockups.map((mockup, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-gray-900 truncate">{mockup.name || `Mockup ${idx + 1}`}</p>
                                      <p className="text-xs text-gray-500 truncate">{mockup.image_url || mockup.thumbnail_url || 'No URL'}</p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditForm({
                                          ...editForm,
                                          mockups: editForm.mockups.filter((_, i) => i !== idx)
                                        });
                                      }}
                                      className="ml-2 px-2 py-1 text-xs text-red-600 hover:text-red-800"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="p-2 bg-gray-50 rounded-md border border-gray-200">
                            {editForm.mockups.length > 0 ? (
                              <div className="space-y-1">
                                {editForm.mockups.map((mockup, idx) => (
                                  <p key={idx} className="text-xs text-gray-600 truncate">
                                    {mockup.name || `Mockup ${idx + 1}`}
                                  </p>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">No mockups assigned</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Visual Indicator */}
                      {editForm.price && editForm.price.trim() !== '' && editForm.mockups.length > 0 && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs text-blue-800">
                            ✓ This design will use mockup-only mode (no format selection)
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleSave(design.id)}
                        disabled={saving}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={saving}
                        className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {saveSuccess === design.id && (
                      <p className="text-xs text-green-600 text-center">✓ Saved successfully!</p>
                    )}
                  </div>
                ) : (
                  // View Mode
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 font-mono mb-1">{design.id}</p>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {design.title || '(No title)'}
                      </h3>
                      {design.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {design.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Orientation:</span>
                        <button
                          onClick={() => handleToggleOrientation(design.id, design.orientation)}
                          disabled={saving}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            saveSuccess === design.id
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700 cursor-pointer'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title="Click to toggle between horizontal and vertical"
                        >
                          {design.orientation || 'N/A'}
                        </button>
                      </div>
                      
                      {/* Display Tags in View Mode */}
                      {design.tags && (() => {
                        const { majorCategory, minorTags } = parseTags(design.tags);
                        const sortedMinorTags = sortTagsAlphabetically(minorTags);
                        return (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-1">Tags:</p>
                            <div className="flex flex-wrap gap-1">
                              {majorCategory && (
                                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${getCategoryColor(majorCategory)}`}>
                                  {majorCategory}
                                </span>
                              )}
                              {sortedMinorTags.slice(0, 3).map((tag, idx) => (
                                <span key={idx} className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                                  {tag}
                                </span>
                              ))}
                              {sortedMinorTags.length > 3 && (
                                <span className="inline-block px-2 py-0.5 text-xs text-gray-500">
                                  +{sortedMinorTags.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleEdit(design)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Edit
                      </button>
                      <a
                        href={`/design/${design.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 transition-colors text-center"
                      >
                        View in Shop
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredDesigns.length === 0 && !loading && (
          <div className="text-center py-12">
            {searchQuery.trim() || orientationFilter || selectedTag ? (
              <div>
                <p className="text-gray-500 text-lg mb-2">
                  No designs found
                  {searchQuery.trim() && ` matching "${searchQuery}"`}
                  {orientationFilter && ` with ${orientationFilter} orientation`}
                  {selectedTag && ` with tag "${selectedTag}"`}
                </p>
                <div className="flex gap-4 justify-center">
                  {searchQuery.trim() && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear search
                    </button>
                  )}
                  {orientationFilter && (
                    <button
                      onClick={() => setOrientationFilter(null)}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear orientation
                    </button>
                  )}
                  {selectedTag && (
                    <button
                      onClick={() => setSelectedTag(null)}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Clear tag filter
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-lg">No designs available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;

